import React from 'react';

import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { Circle, CircleMarker, Tooltip as LeafletTooltip, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';

import { Box, Typography } from '@mui/material';

// Fix default marker icon: Leaflet's default icon paths are resolved via CSS url()
// which breaks under webpack/rspack bundlers that rewrite asset paths. We import
// the PNGs directly so the bundler resolves correct URLs at build time.
const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface OfferPoint {
  id: string;
  title: string;
  latitude: number | null;
  longitude: number | null;
  relevanceScore: number;
}

interface ProximityMapProps {
  latitude: number;
  longitude: number;
  name: string;
  radiusMeters?: number;
  offers?: OfferPoint[];
  totalOffers?: number;
  onOfferClick?: (offerId: string) => void;
}

export default function ProximityMap({
  latitude,
  longitude,
  name,
  radiusMeters = 5000,
  offers = [],
  totalOffers,
  onOfferClick,
}: ProximityMapProps) {
  if (latitude == null || longitude == null) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="caption" color="text.disabled">
          No coordinates
        </Typography>
      </Box>
    );
  }

  // Filter offers with valid coordinates
  const offerPoints = offers.filter(
    (o): o is OfferPoint & { latitude: number; longitude: number } => o.latitude != null && o.longitude != null,
  );

  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={13}
      scrollWheelZoom={false}
      style={{ width: '100%', height: '100%' }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />

      {/* 5km proximity radius */}
      <Circle
        center={[latitude, longitude]}
        radius={radiusMeters}
        pathOptions={{
          color: '#1A56DB',
          fillColor: '#1A56DB',
          fillOpacity: 0.06,
          weight: 1.5,
          dashArray: '6 4',
        }}
      />

      {/* Offer location points */}
      {offerPoints.map((offer) => (
        <CircleMarker
          key={offer.id}
          center={[offer.latitude, offer.longitude]}
          radius={8}
          pathOptions={{
            color: '#fff',
            fillColor: '#059669',
            fillOpacity: 0.85,
            weight: 1.5,
          }}
          eventHandlers={{
            click: () => onOfferClick?.(offer.id),
          }}
        >
          <LeafletTooltip direction="top" offset={[0, -8]} sticky>
            <span style={{ fontSize: '0.7rem', fontWeight: 600 }}>
              {offer.title.length > 50 ? `${offer.title.substring(0, 50)}...` : offer.title}
            </span>
            <br />
            <Typography variant="caption" color="text.secondary" component="span" sx={{ fontSize: '0.6rem' }}>
              {Math.round(offer.relevanceScore * 100)}% relevance
            </Typography>
          </LeafletTooltip>
        </CircleMarker>
      ))}

      {/* Attraction marker (on top) */}
      <Marker position={[latitude, longitude]} icon={defaultIcon}>
        <Popup>
          <strong>{name}</strong>
          <br />
          <Typography variant="caption" color="text.secondary" component="span" sx={{ fontSize: '0.75rem' }}>
            {totalOffers != null && totalOffers > offerPoints.length
              ? `Showing ${offerPoints.length} of ${totalOffers} offers within ${radiusMeters / 1000}km`
              : `${offerPoints.length} offers within ${radiusMeters / 1000}km`}
          </Typography>
        </Popup>
      </Marker>
    </MapContainer>
  );
}
